import { type FC, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import styles from './Header.module.css';

interface HeaderProps {
  onOpenRightDrawer: (content: { title: string; component: ReactNode }) => void;
  rightDrawerOpen?: boolean;
}

interface Notification {
  id: string | number;
  title: string;
  message: string;
  read: boolean;
  link?: string;
  created_at: string;
}

interface PageInfo {
  title: string;
  subtitle: string;
}

const PAGE_MAP: Record<string, PageInfo> = {
  '/': { title: 'Specifications', subtitle: 'Upload and manage API specifications' },
  '/specifications': { title: 'Specifications', subtitle: 'Upload and manage API specifications' },
  '/proxy-config': { title: 'Proxy Settings', subtitle: 'Configure proxy profiles and target servers' },
  '/mle-config': { title: 'MLE Configuration', subtitle: 'Configure Message Level Encryption settings' },
  '/business-flows': { title: 'Business Flows', subtitle: 'Design and manage API testing workflows' },
  '/strategies': { title: 'Strategies', subtitle: 'AI-powered security test strategies' },
  '/passive-scans': { title: 'Passive Scans', subtitle: 'Monitor API traffic for security issues' },
  '/test-hypotheses': { title: 'Test Hypotheses', subtitle: 'Review and manage test hypotheses' },
  '/test-execution': { title: 'Test Execution', subtitle: 'Run security tests and view results' },
  '/vulnerabilities': { title: 'Vulnerabilities', subtitle: 'Track and manage discovered vulnerabilities' },
  '/settings': { title: 'Settings', subtitle: 'Configure application settings' },
  '/organizations': { title: 'Organizations', subtitle: 'Manage organizations and tenants' },
  '/users': { title: 'Users', subtitle: 'Manage users and permissions' },
};

export const Header: FC<HeaderProps> = ({ onOpenRightDrawer }) => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const loadNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/notifications`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  }, []);

  // Load notifications
  useEffect(() => {
    if (user && dropdownOpen) {
      loadNotifications();
    }
  }, [user, dropdownOpen, loadNotifications]);

  const markNotificationRead = async (notificationId: string | number) => {
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      loadNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const formatNotificationTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return `${Math.floor(diffMinutes / 1440)}d ago`;
  };

  // Get current page title based on pathname
  const getPageInfo = (path: string): PageInfo => {
    if (PAGE_MAP[path]) return PAGE_MAP[path];
    if (path.startsWith('/specifications/'))
      return { title: 'Specification Configuration', subtitle: 'Configure specification settings' };
    if (path.startsWith('/vulnerabilities/'))
      return { title: 'Vulnerability Detail', subtitle: 'View vulnerability details and remediation' };
    if (path.startsWith('/hypotheses/'))
      return { title: 'Hypothesis Detail', subtitle: 'Security test hypothesis and linked tests' };
    if (path.startsWith('/tests/')) return { title: 'Test Detail', subtitle: 'Test code and linked hypothesis' };
    return { title: 'Security Testing', subtitle: 'by Wallarm' };
  };

  const { title, subtitle } = getPageInfo(location.pathname);

  useEffect(() => {
    document.title = `${title} — Security Testing by Wallarm`;
  }, [title]);

  const handleHelp = () => {
    onOpenRightDrawer({
      title: 'Help & Documentation',
      component: (
        <div>
          <h4>Getting Started</h4>
          <p>Follow these steps to start testing your APIs:</p>
          <ol>
            <li>Upload your OpenAPI spec or Postman collection</li>
            <li>Configure MLE settings if required</li>
            <li>Generate AI-powered security tests</li>
            <li>Review and run the tests</li>
            <li>Export detailed reports</li>
          </ol>

          <h4>Key Features</h4>
          <ul>
            <li>OWASP API Top 10 Testing</li>
            <li>MLE Encryption Testing</li>
            <li>Business Logic Validation</li>
            <li>Visual Flow Editor</li>
            <li>CI/CD Integration</li>
          </ul>
        </div>
      ),
    });
  };

  const userInitials = !user ? '?' : (user.firstName || user.username || '').charAt(0).toUpperCase();
  const userRoleDisplay = !user?.role
    ? 'User'
    : user.role
        .replace('_', ' ')
        .split(' ')
        .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <header className={styles.headerContainer}>
      <div className={styles.headerTitle}>
        <h1 className={styles.pageTitle}>{title}</h1>
        <span className={styles.pageSubtitle}>{subtitle}</span>
      </div>

      <div className={styles.headerActions}>
        {user && (
          <div ref={dropdownRef} className={styles.userInfoWrapper}>
            <div className={styles.userInfo} onClick={() => setDropdownOpen(!dropdownOpen)}>
              <div className={styles.userAvatar}>{userInitials}</div>
              <div className={styles.userDetails}>
                <div className={styles.userName}>{user.username}</div>
                <div className={styles.userRole}>({userRoleDisplay})</div>
              </div>
              {unreadCount > 0 && <div className={styles.notificationBadge}>{unreadCount}</div>}
            </div>

            <div className={styles.userDropdown} data-open={dropdownOpen ? 'true' : 'false'}>
              {/* Notifications Section */}
              <div className={styles.dropdownSection}>
                <h4 className={styles.dropdownSectionTitle}>Notifications</h4>
                {notifications.length === 0 ? (
                  <div className={styles.emptyState}>No new notifications</div>
                ) : (
                  <div className={styles.notificationList}>
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={styles.notificationItem}
                        data-unread={!notification.read ? 'true' : 'false'}
                        onClick={() => {
                          markNotificationRead(notification.id);
                          if (notification.link) {
                            navigate(notification.link);
                            setDropdownOpen(false);
                          }
                        }}
                      >
                        <div className={styles.notificationTitle}>{notification.title}</div>
                        <div className={styles.notificationMessage}>{notification.message}</div>
                        <div className={styles.notificationTime}>{formatNotificationTime(notification.created_at)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Organization Details */}
              <div className={styles.dropdownSection}>
                <h4 className={styles.dropdownSectionTitle}>Organization</h4>
                <div className={styles.orgInfo}>
                  <div className={styles.orgInfoFlex}>
                    <div className={styles.orgLabel}>Organization</div>
                    <div className={styles.orgValue}>{user.organization_name || 'N/A'}</div>
                  </div>
                </div>
              </div>

              {/* Logout */}
              <div className={styles.dropdownSection}>
                <button
                  className={styles.dropdownButton}
                  data-variant="danger"
                  onClick={() => {
                    logout();
                    navigate('/login');
                    setDropdownOpen(false);
                  }}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}

        <button className={styles.themeToggle} onClick={toggleDarkMode} title="Toggle dark mode">
          {isDarkMode ? '☀' : '☾'}
        </button>

        <button className={styles.actionButton} onClick={handleHelp}>
          Help
        </button>
      </div>
    </header>
  );
};
